import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, BrainCircuit, BriefcaseBusiness, CalendarDays,
  CheckCircle2, Code2, Gamepad2, GraduationCap, Heart, Lock, MapPin,
  MessageCircle, Network, Palette, Rocket, ShieldCheck, Sparkles, Ticket,
  Trophy, UserRoundCheck, Users
} from 'lucide-react';
import useInView from '../hooks/useInView';

const SectionLabel = ({ children }) => (
  <p className="mb-3 font-outfit text-xs font-semibold uppercase tracking-[0.2em] text-purple-400">
    {children}
  </p>
);

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.08);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Status = ({ children, tone = 'purple' }) => {
  const tones = {
    cyan: 'border-cyan-400/35 bg-cyan-400/10 text-cyan-200',
    purple: 'border-purple-400/35 bg-purple-400/10 text-purple-200',
    pink: 'border-fuchsia-400/35 bg-fuchsia-400/10 text-fuchsia-200',
    gold: 'border-amber-400/35 bg-amber-400/10 text-amber-200',
  };
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Feature = ({ icon: Icon, title, children, tone = 'purple' }) => {
  const tones = {
    cyan: 'border-cyan-400/20 bg-cyan-400/10 text-cyan-300',
    purple: 'border-purple-400/20 bg-purple-400/10 text-purple-300',
    pink: 'border-fuchsia-400/20 bg-fuchsia-400/10 text-fuchsia-300',
    gold: 'border-amber-400/20 bg-amber-400/10 text-amber-300',
  };
  return (
    <div className="flex items-start gap-3">
      <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border ${tones[tone]}`}>
        <Icon size={17} />
      </div>
      <div>
        <h3 className="mb-1 font-outfit text-sm font-semibold text-white">{title}</h3>
        <p className="text-xs leading-relaxed text-white/48">{children}</p>
      </div>
    </div>
  );
};

const ProductHeader = ({ id, label, title, status, statusTone, subtitle, children }) => (
  <div id={id} className="scroll-mt-24">
    <SectionLabel>{label}</SectionLabel>
    <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="font-outfit text-4xl font-black tracking-tight text-white sm:text-5xl">{title}</h2>
        <p className="mt-2 max-w-3xl text-base leading-relaxed text-white/55">{subtitle}</p>
      </div>
      <Status tone={statusTone}>{status}</Status>
    </div>
    {children}
  </div>
);

const ecosystemTabs = [
  { href: '#kids', label: 'ROMIND KIDS', status: 'Pre-release' },
  { href: '#teens', label: 'ROMIND TEENS', status: 'In development' },
  { href: '#parents-network', label: 'ROMIND PARENTS', status: 'In development' },
  { href: '#family-circle', label: 'FAMILY CIRCLE', status: 'Ecosystem' },
];

const revenueEngines = [
  { icon: Users, title: 'Family membership', desc: 'Family ID, Family Subscription and Family Pass with member pricing, partner benefits and closed-event access.' },
  { icon: Ticket, title: 'Arena & live events', desc: 'Online preparation and team-building connected to ticketed offline competitions, showcases, ceremonies and family experiences.' },
  { icon: GraduationCap, title: 'Academy programs', desc: 'Structured learning paths, project labs, mentorship and portfolio-building experiences.' },
  { icon: BriefcaseBusiness, title: 'Talent & partnerships', desc: 'Family-safe collaborations with educators, event partners and creative industries through ROMIND Talent Studio.' },
  { icon: Rocket, title: 'Venture Studio pathway', desc: 'A future pathway from approved youth ideas to supervised MVP development and long-term ecosystem value.' },
];

const Ecosystem = () => (
  <main className="min-h-screen bg-[#05050A] pt-16 text-white">
    <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-28">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(168,85,247,0.24),transparent_34%),radial-gradient(circle_at_80%_25%,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_50%_90%,rgba(217,70,239,0.12),transparent_38%)]" />
      <div className="pointer-events-none absolute left-1/2 top-16 h-72 w-72 -translate-x-1/2 rounded-full border border-purple-400/10 shadow-[0_0_120px_rgba(168,85,247,0.18)]" />
      <div className="relative mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
        <FadeIn>
          <Status tone="cyan">Family-scale vision</Status>
          <h1 className="mx-auto mt-6 max-w-5xl font-outfit text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            One family. Three age-aware worlds. <span className="gradient-text">One protected circle.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg">
            ROMIND begins with a safer social universe for children and grows with the whole family — from creativity and friendship, to teen achievement and future-building, to a trusted parent community online and offline.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#family-circle" className="inline-flex items-center gap-2 rounded-full border border-purple-400/45 bg-purple-600/80 px-6 py-3 text-sm font-semibold transition hover:bg-purple-500">
              Explore the Family Circle <ArrowRight size={15} />
            </a>
            <Link to="/contact" className="rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white/85 transition hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-white">
              Partnerships & enquiries
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="sticky top-16 z-30 border-b border-white/8 bg-[#05050A]/90 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
        {ecosystemTabs.map((tab) => (
          <a key={tab.href} href={tab.href} className="min-w-fit rounded-xl border border-white/8 bg-white/[0.035] px-4 py-2 transition hover:border-purple-400/35 hover:bg-purple-400/10">
            <span className="block font-outfit text-xs font-bold text-white">{tab.label}</span>
            <span className="mt-0.5 block text-[10px] uppercase tracking-wider text-white/35">{tab.status}</span>
          </a>
        ))}
      </div>
    </section>

    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The growth path</SectionLabel>
          <h2 className="max-w-4xl font-outfit text-3xl font-bold tracking-tight text-white sm:text-4xl">
            The platform changes as children grow — the relationship does not disappear.
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/50">
            ROMIND KIDS V1 comes first, followed by Live experiences, ROMIND TEENS, and then the independent ROMIND PARENTS social network that completes the Family Circle.
          </p>
        </FadeIn>
        <div className="mt-10 grid gap-4 md:grid-cols-4">
          {[
            ['01', 'KIDS V1', 'Safety, creativity, friendship, clubs, challenges and Academy.'],
            ['02', 'KIDS + LIVE', 'Scheduled community experiences and the pathway to ROMIND KIDS Arena.'],
            ['03', 'TEENS', 'Future projects and achievement—balanced with identity, first feelings, relationships and communication.'],
            ['04', 'PARENTS', 'A full adult social network, Parent Academy, practical family services and offline community life.'],
          ].map(([num, title, desc], i) => (
            <FadeIn key={num} delay={i * 80}>
              <div className="glass-card h-full p-5">
                <p className="font-outfit text-xs font-bold tracking-[0.18em] text-purple-400">{num}</p>
                <h3 className="mt-3 font-outfit text-lg font-bold text-white">{title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/45">{desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-cyan-400/10 bg-[#071018] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <ProductHeader
            id="kids"
            label="Ages 9–14 · The foundation"
            title="ROMIND KIDS"
            status="Pre-release"
            statusTone="cyan"
            subtitle="A child-specific social, creative and learning universe designed to protect without isolating. Children can communicate, make friends, create, play and develop inside an age-aware environment."
          >
            <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
              <div className="grid gap-5 sm:grid-cols-2">
                <Feature icon={ShieldCheck} title="Safety built into the product" tone="cyan">Age-aware spaces, friend-based communication, report and block flows, parent controls and RONI safety guidance.</Feature>
                <Feature icon={MessageCircle} title="A real social world" tone="cyan">Profiles, friends, Messenger, Stories, Smart Feed and interest-based Clubs — not a silent room with white walls.</Feature>
                <Feature icon={Palette} title="Creation over passive scrolling" tone="cyan">Posts, art, media, challenges and creative tools turn screen time into expression and participation.</Feature>
                <Feature icon={GraduationCap} title="Academy & progress" tone="cyan">Project-based learning, missions, teamwork, badges, rewards and a growing portfolio of achievements.</Feature>
                <Feature icon={Heart} title="Meaningful connection" tone="cyan">Times helps children share a part of their time with friends as a positive social gesture.</Feature>
                <Feature icon={BrainCircuit} title="RONI companion" tone="cyan">A supportive AI layer for guidance, reflection, learning support and safer decision-making.</Feature>
              </div>
              <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400/10 via-purple-500/8 to-transparent p-6">
                <div className="flex items-center gap-3">
                  <Trophy className="text-cyan-300" size={24} />
                  <h3 className="font-outfit text-xl font-bold">ROMIND KIDS Arena</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/55"><strong className="text-white">The Arena is a real-world event system—not another screen inside the app.</strong> ROMIND KIDS provides the protected digital road to the stage; the decisive performances, exhibitions and competitions happen offline.</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <div className="rounded-2xl border border-cyan-400/15 bg-black/15 p-4">
                    <p className="font-outfit text-xs font-bold uppercase tracking-wider text-cyan-300">Online preparation</p>
                    <p className="mt-2 text-xs leading-relaxed text-white/52">Discover a direction, practise through Clubs and Challenges, create a portfolio, find teammates, register with a parent, receive announcements and prepare for the event.</p>
                  </div>
                  <div className="rounded-2xl border border-fuchsia-400/15 bg-black/15 p-4">
                    <p className="font-outfit text-xs font-bold uppercase tracking-wider text-fuchsia-300">Offline Arena</p>
                    <p className="mt-2 text-xs leading-relaxed text-white/52">Age-based solo, duo and team competitions; live performances, project showcases, exhibitions, ceremonies and family attendance in a supervised venue.</p>
                  </div>
                </div>
                <div className="mt-5 space-y-3">
                  <p className="text-xs leading-relaxed text-white/58"><strong className="text-cyan-200">Technology & play:</strong> Coding, AI, Build-a-World, Gaming Light and creator challenges.</p>
                  <p className="text-xs leading-relaxed text-white/58"><strong className="text-fuchsia-200">Creative & talent:</strong> Music & Talent, vocals, Creative Art, drawing, image, short video, mixed media and Creator Cup showcases.</p>
                </div>
              </div>
            </div>
            <div className="mt-8 rounded-3xl border border-amber-400/20 bg-gradient-to-r from-amber-400/[0.08] via-fuchsia-400/[0.08] to-cyan-400/[0.08] p-6">
              <div className="grid gap-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center">
                <div>
                  <div className="flex items-center gap-3"><Sparkles className="text-amber-300" size={23} /><h3 className="font-outfit text-xl font-bold">ROMIND Talent Studio</h3></div>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-amber-200/80">From a child's first work to a protected professional showcase</p>
                </div>
                <p className="text-sm leading-relaxed text-white/54">A supervised creative pathway for young musicians, vocalists, artists, filmmakers and other creators. The Studio helps develop work, build a portfolio, prepare a stage or exhibition presentation and—only with parent consent and safeguarding—introduce exceptional talent to verified mentors, promoters and professionals in music, film and creative industries. Visibility is an opportunity, never a promised contract.</p>
              </div>
            </div>
          </ProductHeader>
        </FadeIn>
      </div>
    </section>

    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <ProductHeader
            id="teens"
            label="Ages 15–18 · The future track"
            title="ROMIND TEENS"
            status="In development"
            statusTone="purple"
            subtitle="A more mature social network built around two equally important parts of growing up: creating a future—and understanding yourself, your first feelings, relationships and place in the world."
          >
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-cyan-400/18 bg-gradient-to-br from-cyan-400/[0.09] via-purple-500/[0.08] to-transparent p-6">
                <div className="flex items-center gap-3"><Rocket className="text-cyan-300" size={24} /><h3 className="font-outfit text-xl font-bold">01 · Future, projects & achievement</h3></div>
                <p className="mt-3 text-sm leading-relaxed text-white/54">Teens explore what they can become by building real things with others—not by being pushed into a miniature university.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Feature icon={Trophy} title="Achievement & leadership" tone="cyan">Advanced challenges, team roles, certifications, visible progress and meaningful recognition.</Feature>
                  <Feature icon={BriefcaseBusiness} title="Projects & portfolio" tone="cyan">Supervised product, creative and social projects; mentor feedback, presentation skills and evidence of progress.</Feature>
                  <Feature icon={Code2} title="Future skills" tone="cyan">Coding, design, testing, digital safety, media, research and collaborative problem-solving as possible directions—not the whole identity of TEENS.</Feature>
                  <Feature icon={Rocket} title="First-startup pathway" tone="cyan">For teens who choose it: idea, team, research, prototype and pitch, without promising that every project becomes a company.</Feature>
                </div>
              </div>
              <div className="rounded-3xl border border-fuchsia-400/18 bg-gradient-to-br from-fuchsia-400/[0.10] via-purple-500/[0.08] to-transparent p-6">
                <div className="flex items-center gap-3"><Heart className="text-fuchsia-300" size={24} /><h3 className="font-outfit text-xl font-bold">02 · Identity, first feelings & communication</h3></div>
                <p className="mt-3 text-sm leading-relaxed text-white/54">Growing up is also learning who you are, how to communicate, how to understand attraction and boundaries, and how to build healthy relationships.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Feature icon={Sparkles} title="Self-discovery" tone="pink">Interests, values, confidence, self-expression and a more mature identity—not a stretched children's profile.</Feature>
                  <Feature icon={Heart} title="First Feelings" tone="pink">Age-aware reflection on attraction, emotions and first relationships inside a carefully protected environment.</Feature>
                  <Feature icon={ShieldCheck} title="Healthy relationships" tone="pink">Consent, boundaries, respect, conflict skills and additional behavioral-pattern safety around sensitive interactions.</Feature>
                  <Feature icon={MessageCircle} title="Real communication" tone="pink">Friendship, dialogue, collaboration and support—learning to interact with the world, not only completing tasks.</Feature>
                </div>
              </div>
            </div>
            <div className="mt-8 rounded-3xl border border-purple-400/18 bg-[#0b0817] p-6">
              <div className="grid gap-6 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
                <div>
                  <div className="flex items-center gap-3"><Gamepad2 className="text-fuchsia-300" size={23} /><h3 className="font-outfit text-xl font-bold">ROMIND TEENS Arena</h3></div>
                  <p className="mt-3 text-sm leading-relaxed text-white/52">Online, teens discover a direction, assemble teams, develop work, receive mentorship and register. Offline, they meet on a real stage for competitions, performances, exhibitions, pitches, ceremonies and industry-safe showcases.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-cyan-400/15 bg-cyan-400/[0.05] px-4 py-4"><p className="text-xs font-bold text-cyan-200">FUTURE & INNOVATION</p><p className="mt-2 text-xs leading-relaxed text-white/55">Cyber Defense, Game Jam, AI Battle, Advanced Coding, Startup Pitch and Gaming Cup.</p></div>
                  <div className="rounded-xl border border-fuchsia-400/15 bg-fuchsia-400/[0.05] px-4 py-4"><p className="text-xs font-bold text-fuchsia-200">CREATIVE & TALENT</p><p className="mt-2 text-xs leading-relaxed text-white/55">Music, vocals, art, design, photography, film, short video, mixed media and live creator showcases through ROMIND Talent Studio.</p></div>
                </div>
              </div>
            </div>
          </ProductHeader>
        </FadeIn>
      </div>
    </section>

    <section className="border-y border-fuchsia-400/10 bg-[#120814] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <ProductHeader
            id="parents-network"
            label="Adults only · Family support"
            title="ROMIND PARENTS"
            status="In development"
            statusTone="pink"
            subtitle="A complete, adults-only social network created specifically for parents—not an add-on to KIDS or TEENS and not a control app. Parents can build their own community, discuss family life and solve many real questions without leaving the platform."
          >
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="glass-card p-6">
                <Lock className="text-fuchsia-300" size={23} />
                <h3 className="mt-4 font-outfit text-lg font-bold">A real parent social network</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/48">Every parent has a profile and feed, can follow other parents, join thematic communities, ask questions, share experience and build genuine social or professional connections.</p>
                <div className="mt-5 space-y-3">
                  {['Parenting and everyday family life', 'Relationships with children and teenagers', 'Growing up, conflicts and difficult periods', 'Education, health, routines and emotional wellbeing', 'Relationships between parents and family dynamics', 'Local communities and trusted recommendations'].map((item) => <p key={item} className="flex gap-2 text-xs text-white/58"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-fuchsia-300" />{item}</p>)}
                </div>
              </div>
              <div className="glass-card p-6">
                <BookOpen className="text-cyan-300" size={23} />
                <h3 className="mt-4 font-outfit text-lg font-bold">ROMIND Parent Academy</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/48">Verified knowledge, practical lessons and direct support from qualified doctors, psychologists, educators, lawyers and crisis specialists.</p>
                <div className="mt-5 space-y-3">
                  {['Lectures, courses, live sessions and consultations', 'Child development, mental health and parenting skills', 'Education, health, digital and relationship safety', 'Practical recommendations for a specific family situation', 'Verified expert content—not anonymous internet advice'].map((item) => <p key={item} className="flex gap-2 text-xs text-white/58"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-cyan-300" />{item}</p>)}
                </div>
              </div>
              <div className="glass-card p-6">
                <BriefcaseBusiness className="text-purple-300" size={23} />
                <h3 className="mt-4 font-outfit text-lg font-bold">Help without leaving the platform</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/48">ROMIND PARENTS is designed to move from “I have a problem” to a clear next step inside one trusted environment.</p>
                <div className="mt-5 space-y-3">
                  {['A dedicated legal-support direction', 'Divorce, custody and child-related questions', 'Family conflict and crisis support', 'Domestic-violence support and safe routing', 'Answers, situation reviews and specialist referrals'].map((item) => <p key={item} className="flex gap-2 text-xs text-white/58"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-purple-300" />{item}</p>)}
                </div>
              </div>
              <div className="glass-card p-6">
                <MapPin className="text-amber-300" size={23} />
                <h3 className="mt-4 font-outfit text-lg font-bold">Parent and family life offline</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/48">A strong family network should exist beyond a screen: known people, qualified experts, trusted places and experiences families can share.</p>
                <div className="mt-5 space-y-3">
                  {['Parent-only meetings and support circles', 'Expert workshops, lectures and local consultations', 'Parent-and-child programs and family days', 'Family entertainment weekends', 'Arena events, ceremonies and closed member gatherings'].map((item) => <p key={item} className="flex gap-2 text-xs text-white/58"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-amber-300" />{item}</p>)}
                </div>
              </div>
            </div>
            <div className="mt-7 rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[0.06] px-5 py-4">
              <p className="text-sm leading-relaxed text-white/58"><strong className="text-white">One non-negotiable boundary:</strong> ROMIND PARENTS is a full social network for verified adults, while KIDS and TEENS keep their own protected, age-specific spaces. Family connection does not mean shared access to children's social worlds.</p>
            </div>
          </ProductHeader>
        </FadeIn>
      </div>
    </section>

    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <ProductHeader
            id="family-circle"
            label="The umbrella architecture"
            title="ROMIND FAMILY CIRCLE"
            status="Ecosystem in development"
            statusTone="gold"
            subtitle="Family Circle is not a fourth social network. It is the membership and shared family architecture connecting ROMIND KIDS, ROMIND TEENS and ROMIND PARENTS through one Family ID, one long-term relationship and privileged access to the wider ROMIND world."
          >
            <div className="relative mt-10">
              <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-cyan-400/0 via-purple-400/50 to-fuchsia-400/0 lg:block" />
              <div className="relative grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { icon: Sparkles, title: 'KIDS', sub: 'Create · connect · belong', tone: 'text-cyan-300 border-cyan-400/20' },
                  { icon: Rocket, title: 'TEENS', sub: 'Grow · discover · build', tone: 'text-purple-300 border-purple-400/20' },
                  { icon: Users, title: 'PARENTS', sub: 'Connect · learn · solve', tone: 'text-fuchsia-300 border-fuchsia-400/20' },
                  { icon: Network, title: 'FAMILY ID', sub: 'One protected family relationship', tone: 'text-amber-300 border-amber-400/25' },
                ].map((item) => (
                  <div key={item.title} className={`rounded-3xl border bg-[#090813] p-7 text-center ${item.tone}`}>
                    <item.icon className="mx-auto" size={28} />
                    <h3 className="mt-4 font-outfit text-2xl font-black text-white">{item.title}</h3>
                    <p className="mt-2 text-xs uppercase tracking-wider text-white/42">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Feature icon={UserRoundCheck} title="Family ID → Subscription → Pass" tone="gold">Linked family membership with clear access boundaries, digital or physical member cards and benefits that grow with the household.</Feature>
              <Feature icon={Rocket} title="KIDS-to-TEENS continuity" tone="gold">The child's achievements, interests and family relationship can continue into the next age-specific social network.</Feature>
              <Feature icon={Ticket} title="Member event access" tone="gold">Special family prices, partner discounts, ticket offers and access to selected closed member-only events.</Feature>
              <Feature icon={CalendarDays} title="Family entertainment weekends" tone="gold">Book shared weekends, workshops, local activities, Arena attendance and parent-and-child programs in one place.</Feature>
              <Feature icon={Users} title="Attend together" tone="gold">Parents, children and teens can participate in the wider event network as a family while using separate social spaces online.</Feature>
              <Feature icon={ShieldCheck} title="One trusted relationship" tone="gold">Common safeguarding and verified family connection without giving adults access to children's private social environments.</Feature>
            </div>
            <div className="mt-7 rounded-2xl border border-amber-400/20 bg-amber-400/[0.06] px-5 py-4 text-center">
              <p className="text-sm font-semibold leading-relaxed text-white/72">One family account can discover an event, register a child or team, buy a family ticket, attend a closed weekend, meet other parents and continue the relationship as the child grows.</p>
            </div>
          </ProductHeader>
        </FadeIn>
      </div>
    </section>

    <section className="border-y border-white/5 bg-[#080711] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Scalable business architecture</SectionLabel>
          <h2 className="max-w-4xl font-outfit text-3xl font-bold tracking-tight sm:text-4xl">One trusted relationship creates several complementary growth engines.</h2>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/50">These are product pathways, not a public revenue forecast. Their value is that they reinforce the same family ecosystem instead of competing for attention as disconnected products.</p>
        </FadeIn>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {revenueEngines.map((item, i) => (
            <FadeIn key={item.title} delay={i * 70}>
              <div className="glass-card h-full p-5">
                <item.icon className="text-purple-300" size={22} />
                <h3 className="mt-4 font-outfit text-base font-bold">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/45">{item.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 text-center">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Built from one clear belief</SectionLabel>
          <h2 className="font-outfit text-4xl font-black tracking-tight sm:text-5xl">A safer internet should not mean a smaller childhood.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/52">ROMIND is building a protected digital and real-world circle where children can belong, teens can build their future, and parents can find trusted people and support.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/features" className="inline-flex items-center gap-2 rounded-full border border-purple-400/45 bg-purple-600/80 px-6 py-3 text-sm font-semibold transition hover:bg-purple-500">Explore ROMIND KIDS <ArrowRight size={15} /></Link>
            <Link to="/contact" className="rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white/85 transition hover:bg-white/10">Contact the founder-led team</Link>
          </div>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default Ecosystem;
